import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { thousandFormatter } from "~/utils/formatter";
import { useTranslation, Trans } from "react-i18next";
import { useGlobalStore } from "~/store";
import useAlert from "~/common/hooks/useAlert";
import i18n from "i18next";

// Components
import NavbarAndSidebarLayout from "~/layouts/NavbarAndSidebarLayout";
import PageHeader from "~/common/components/PageHeader";
import Form from "~/common/components/Form";
import Select from "~/common/components/Select";
import ModalTemplate from "~/common/components/ModalPortal/ModalTemplate";
import PortfolioMemo from "~/pages/portfolio/PortfolioMemo";

// Assets
import "~/assets/sass/pages/portfolio/portfolioList.scss";
import darkLogoImg from "~/assets/img/defaultImg/dark-logo-square.png";
// import lightLogoImg from "~/assets/img/defaultImg/light-logo-square.png";
import connectImg from "~/assets/img/company-connect-img.png";
import { CautionIcon, CheckIcon, ExcelIcon, SearchIcon } from "~/assets/svg/InterfaceIcon";
import { SolidCloseIcon } from "~/assets/svg/CloseIcon";

// Assets
import useRequest from "~/common/hooks/useRequest";
import { API_STATE } from "~/common/constants/state";

// Pages
import Filter from "./components/Filter";
import ListItem from "./components/ListItem";

const PortfolioList = () => {
	const [
		{
			sidebar: { currentPath },
		},
	] = useGlobalStore();

	const [showAuthAlert, AuthAlert] = useAlert();

	const navigate = useNavigate();
	const listRequest = useRequest("patch", "vc/portfolio/search/st");
	const industryRequest = useRequest("get", "vc/portfolio/industry");
	const [options, setOptions] = useState([]);
	const [formData, setFormData] = useState({
		investStart: "",
		investEnd: "",
		industry: [],
		page: 1,
		size: 1000,
		sortName: "estDate",
		sort: "asc",
		comValue: "",
		name: "",
		steps: [],
	});
	const [portfolioList, setPortfolioList] = useState([]);
	const [portfolioHis, setPortfolioHist] = useState(null);
	const [portfolioTotalCount, setPortfolioTotalCount] = useState(0);
	const [portfolioTotalValues, setPortfolioTotalValues] = useState({});

	const { t } = useTranslation();
	const headerButtons = [
		<button type="button" className="btn-circle" disabled>
			<ExcelIcon />
		</button>,
		<button
			type="button"
			className="btn__solid"
			color="primary"
			onClick={() => {
				if (currentPath.auth !== "edit") {
					showAuthAlert();
					return;
				}
				navigate("/portfolio/create");
			}}
		>
			{t("pages.portfolio.PortfolioList.enroll")}
		</button>,
	];

	const portfolioListForm = useForm({
		defaultValues: {
			dateType: "1",
			investStart: new Date(null),
			investEnd: new Date(),
		},
	});
	const portfolioListSelectForm = useForm();

	const [showCancelAlert, CancelAlert] = useAlert();
	const [companyConnectModal, setCompanyConnectModal] = useState(false);
	const [addPortfolioModal, setAddPortfolioModal] = useState(false);

	const selectOrder = portfolioListSelectForm.watch("orderList");
	const selectInvest = portfolioListSelectForm.watch("investSelect");

	const handleSubmit = (data) => {
		const submitData = {
			...formData,
			...data,
		};

		setFormData(submitData);
		listRequest.call(submitData);
	};

	const handleChangeIndustry = (data) => {
		const submitData = {
			...formData,
			industry: data,
		};

		setFormData(submitData);
		listRequest.call(submitData);
	};

	const handleChangeComValue = (min, max) => {
		const submitData = {
			...formData,
			comValue: [min || null, max || null],
		};

		setFormData(submitData);
		listRequest.call(submitData);
	};

	const handleChangeInvest = (data) => {
		const submitData = {
			...formData,
			steps: data,
		};

		setFormData(submitData);
		listRequest.call(submitData);
	};

	useEffect(() => {
		listRequest.call(formData);
	}, [formData.investEnd, formData.investStart]);

	useEffect(() => {
		if (!selectOrder) return;
		const reqData = { ...formData, sort: selectOrder };
		setFormData(reqData);
		listRequest.call(reqData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectOrder]);

	useEffect(() => {
		if (!selectInvest) return;
		const reqData = { ...formData, sortName: selectInvest };
		setFormData(reqData);
		listRequest.call(reqData);
	}, [selectInvest]);

	useEffect(() => {
		listRequest.singleCall({
			...formData,
			investEnd: "",
			investStart: "",
		});
		industryRequest.singleCall();
	}, []);

	useEffect(() => {
		switch (listRequest.state) {
			case API_STATE.done:
				const requestPortfolioList = listRequest.response?.data;
				setPortfolioList(requestPortfolioList.totalList);
				setPortfolioTotalCount(requestPortfolioList.totalCount);
				if (
					requestPortfolioList.totalValues &&
					requestPortfolioList.totalValues.length > 0
				) {
					setPortfolioTotalValues(requestPortfolioList.totalValues[0]);
				} else {
					setPortfolioTotalValues({
						totalComValue: 0,
						totalVcInvest: 0,
						totalWithdraw: 0,
					});
				}
				setPortfolioHist(requestPortfolioList.investHistory);
				break;
			default:
				break;
		}
	}, [listRequest.state, listRequest.response, setPortfolioList]);

	useEffect(() => {
		switch (industryRequest.state) {
			case API_STATE.done:
				const industryApiList = industryRequest.response?.data;
				let tmpArr = [];
				industryApiList.map((industArr) => {
					tmpArr.push({
						label: i18n.language === "ko" ? industArr.name : industArr.engName,
						value: industArr.idx,
					});
				});
				setOptions(tmpArr);
				break;
			default:
				break;
		}
	}, [industryRequest.state, industryRequest.response, i18n.language]);

	return (
		<>
			<NavbarAndSidebarLayout menu={["portfolio", "list"]} type={"main"}>
				<PageHeader buttonList={headerButtons} />
				<div className="c__card card-portfolio-list active">
					<Filter
						useForm={portfolioListForm}
						industryOptions={options}
						onSubmit={handleSubmit}
						onChangeIndustry={handleChangeIndustry}
						onChangeComValue={handleChangeComValue}
						onChangeInvest={handleChangeInvest}
						setFormData={setFormData}
						formData={formData}
					/>
				</div>
				<div className="portfolio-wrap">
					<div className="portfolio-top">
						<div className="portfolio-search-unit">
							<span>
								<Trans
									i18nKey="pages.portfolio.PortfolioList.searchResult"
									components={[{ portfolioTotalCount }]}
								/>
							</span>
							{i18n.language === "en" && (
								<span className="search-result-num-unit">{"(Korean Won)"}</span>
							)}
						</div>
						<ul className="total-count-list">
							<li className="total-count-item">
								{t("pages.portfolio.PortfolioList.totalValue")}
								<b>
									{portfolioTotalValues?.totalComValue
										? `${
												thousandFormatter(
													Math.floor(portfolioTotalValues?.totalComValue)
												) || 0
										  }${t("pages.portfolio.PortfolioList.hMillion")}`
										: `${i18n.language === "ko" ? "0억" : "0 million"}`}
								</b>
							</li>
							<li className="total-count-item">
								{t("pages.portfolio.PortfolioList.totalAmount")}
								<b>
									{portfolioTotalValues?.totalVcInvest
										? `${
												thousandFormatter(
													Math.floor(portfolioTotalValues?.totalVcInvest)
												) || 0
										  }${t("pages.portfolio.PortfolioList.hMillion")}`
										: `${i18n.language === "ko" ? "0억" : "0 million"}`}
								</b>
							</li>
							<li className="total-count-item">
								{t("pages.portfolio.PortfolioList.totalRecover")}
								<b>
									{portfolioTotalValues?.totalWithdraw
										? `${
												thousandFormatter(
													Math.floor(portfolioTotalValues?.totalWithdraw)
												) || 0
										  }${t("pages.portfolio.PortfolioList.hMillion")}`
										: `${i18n.language === "ko" ? "0억" : "0 million"}`}
								</b>
							</li>
						</ul>
						<div className="select-group">
							<Form useForm={portfolioListSelectForm}>
								<Select
									name="orderList"
									options={[
										{
											label: t("pages.portfolio.PortfolioList.asc"),
											value: "asc",
										},
										{
											label: t("pages.portfolio.PortfolioList.desc"),
											value: "desc",
										},
									]}
									defaultValue={selectOrder || "asc"}
								/>
								<Select
									name="investSelect"
									options={[
										{
											label: t("pages.portfolio.PortfolioList.investPoint"),
											value: "vcInvestDate",
										},
										{
											label: t("pages.portfolio.PortfolioList.investAmount"),
											value: "value",
										},
										{
											label: t("pages.portfolio.PortfolioList.investStage"),
											value: "step",
										},
										{
											label: t("pages.portfolio.PortfolioList.investValue"),
											value: "comValue",
										},
										{
											label: t(
												"pages.portfolio.PortfolioList.companyHistory"
											),
											value: "estDate",
										},
									]}
									defaultValue={selectInvest || "estDate"}
								/>
							</Form>
						</div>
					</div>
					<ul className="portfolio-list">
						{portfolioList?.length <= 0 && (
							<div className="c__noData">
								<CautionIcon />
								<p className="main">
									{formData?.name?.length > 0
										? t("pages.portfolio.PortfolioList.noMatching")
										: t("pages.portfolio.PortfolioList.noExist")}
								</p>
							</div>
						)}
						{portfolioList?.map((portfolio, index) => {
							const idx = portfolio?.portfolioIdx;
							const keywords = portfolio?.keyword?.split(",");
							const listHist = portfolioHis.filter((l) => l.portfolioIdx === idx);
							const values = [];
							const years = [];
							const data = [];
							const areaData = [
								{ label: "", value: 0 },
								...listHist.map((item) => ({
									label: item.year,
									value: item.value,
								})),
							];

							if (listHist.length > 0) {
								const r = [0, 800, 270, 140, 83, 57, 39, 30, 23, 18, 15];

								listHist.forEach((h) => {
									const year = h.year.substring(0, 4);
									if (years.findIndex((y) => y === year) < 0) {
										years.push(year);
									}
								});

								years.sort((a, b) => {
									return parseInt(a) - parseInt(b);
								});
								years.splice(0, years.length - 10);
								years.forEach((y, y_idx) => {
									values.push({
										label: y,
										x: (y_idx + 1) * years.length * r[years.length],
									});
								});

								values.forEach((v, v_idx) => {
									let investSum = 0;
									listHist.forEach((h) => {
										if (h.year.includes(v.label)) {
											investSum += Number(h.value);
										}
									});
									data.push({
										x: (
											(v_idx + 1) *
											years.length *
											r[years.length]
										).toString(),
										y: "50",
										z: investSum.toString(),
										name: `${thousandFormatter(investSum)}${t(
											"pages.portfolio.PortfolioList.hMillion"
										)}`,
										color: v_idx + 1 === values.length ? "#6B44FF" : "#c9ced6",
									});
								});
							}
							return (
								<ListItem
									key={index}
									item={portfolio}
									keywords={keywords}
									category={values}
									dataSet={data}
									hisArr={listHist}
									areaData={areaData}
									idx={idx}
									onClickItem={() => navigate(`/portfolio/view/${idx}`)}
								/>
							);
						})}
					</ul>
				</div>
			</NavbarAndSidebarLayout>
			{/*포트폴리오 등록 모달*/}
			<ModalTemplate
				modalState={addPortfolioModal}
				setModalToggle={setAddPortfolioModal}
				title={t("pages.portfolio.PortfolioList.enrollPortfolio")}
				className="add-portfolio-modal"
			>
				<div className="modal-content">
					<div className="c__search">
						<input
							className="c__input"
							placeholder={t("pages.portfolio.PortfolioList.inputCompany")}
						/>
						<button type="button" className="btn-search">
							<SearchIcon />
						</button>
					</div>
					<div className="scroll-wrap">
						<ul>
							<li className="company-item">
								<div className="company-info">
									<div className="company-logo">
										<img
											src={darkLogoImg}
											alt={t("pages.portfolio.PortfolioList.logo")}
										/>
										{/*<img src={lightLogoImg} alt="로고이미지" />*/}
									</div>
									<div className="company-data">
										<div className="company-info">
											<span className="company-name">
												{t("pages.portfolio.PortfolioList.bumco")}
											</span>
											<span className="company-info-item">
												{t("pages.portfolio.PortfolioList.appSupply")}
											</span>
											<span className="company-info-item">
												{t("pages.portfolio.PortfolioList.esDate")}
											</span>
										</div>
										<p className="company-explain">
											{t("pages.portfolio.PortfolioList.companyExplain")}
										</p>
										<div className="company-field">
											<span>#Life </span>
											<span>#Shopping </span>
										</div>
									</div>
								</div>
								<div className="btn-wrap">
									<button type="button" className="btn-circle btn-chk active">
										<CheckIcon />
									</button>
								</div>
							</li>
							<li className="company-item">
								<div className="company-info">
									<div className="company-logo">
										<img
											src={darkLogoImg}
											alt={t("pages.portfolio.PortfolioList.logo")}
										/>
										{/*<img src={lightLogoImg} alt="로고이미지" />*/}
									</div>
									<div className="company-data">
										<div className="company-info">
											<span className="company-name">
												{t("pages.portfolio.PortfolioList.illunex")}
												(주)일루넥스
											</span>
											<span className="company-info-item">
												{t("pages.portfolio.PortfolioList.appSupply")}
												응용소프트웨어 개발 및 공급업
											</span>
											<span className="company-info-item">
												{t("pages.portfolio.PortfolioList.esDate")}2년
												11개월
											</span>
										</div>
										<p className="company-explain">
											{t("pages.portfolio.PortfolioList.companyExplain")}
										</p>
										<div className="company-field">
											<span>#Life </span>
											<span>#Shopping </span>
										</div>
									</div>
								</div>
								<div className="btn-wrap">
									<button type="button" className="btn-circle btn-chk">
										{/*<button type="button" className="btn-circle btn-chk active">*/}
										<CheckIcon />
									</button>
								</div>
							</li>
							<li className="company-item">
								<div className="company-info">
									<div className="company-logo">
										<img
											src={darkLogoImg}
											alt={t("pages.portfolio.PortfolioList.logo")}
										/>
										{/*<img src={lightLogoImg} alt="로고이미지" />*/}
									</div>
									<div className="company-data">
										<div className="company-info">
											<span className="company-name">
												{t("pages.portfolio.PortfolioList.bumco")}
											</span>
											<span className="company-info-item">
												{t("pages.portfolio.PortfolioList.appSupply")}
											</span>
											<span className="company-info-item">
												{t("pages.portfolio.PortfolioList.esDate")}
											</span>
										</div>
										<p className="company-explain">
											{t("pages.portfolio.PortfolioList.companyExplain")}
										</p>
										<div className="company-field">
											<span>#Life </span>
											<span>#Shopping </span>
										</div>
									</div>
								</div>
								<div className="btn-wrap">
									<button type="button" className="btn-circle btn-chk active">
										<CheckIcon />
									</button>
								</div>
							</li>
						</ul>
					</div>
					<ul className="c__tag-wrap">
						<li className="tag-item">
							{t("pages.portfolio.PortfolioList.bumco")}
							<button type="button" className="btn-close">
								<SolidCloseIcon />
							</button>
						</li>
						<li className="tag-item">
							{t("pages.portfolio.PortfolioList.illunex")}
							<button type="button" className="btn-close">
								<SolidCloseIcon />
							</button>
						</li>
					</ul>
				</div>
				<div className="modal-footer">
					<button type="button" className="btn__solid" color="gray">
						{t("pages.portfolio.PortfolioList.cancel")}
					</button>
					<button type="button" className="btn__solid" color="primary">
						{t("pages.portfolio.PortfolioList.enroll2")}
					</button>
				</div>
			</ModalTemplate>
			{/*연결하기 모달*/}
			<ModalTemplate
				modalState={companyConnectModal}
				setModalToggle={setCompanyConnectModal}
				title={t("pages.portfolio.PortfolioList.connectModal")}
				width={700}
				className="company-connect-modal"
			>
				<div className="modal-content">
					<dl>
						<dt>
							<Trans
								i18nKey="pages.portfolio.PortfolioList.joyCompany"
								components={[<b />, <br />]}
							/>
						</dt>
						<dd>
							<Trans
								i18nKey="pages.portfolio.PortfolioList.joyDesc"
								components={[<br />]}
							/>
						</dd>
					</dl>
					<div className="img-wrap">
						<img
							src={connectImg}
							alt={t("pages.portfolio.PortfolioList.companyLink")}
						/>
					</div>
				</div>
				<div className="modal-footer">
					<button type="button" className="btn__solid" color="primary">
						{t("pages.portfolio.PortfolioList.joint")}
					</button>
				</div>
			</ModalTemplate>
			{/*신청취소 alert 모달*/}
			<CancelAlert
				type="error"
				message={t("pages.portfolio.PortfolioList.cancelJoint")}
				reject={t("pages.portfolio.PortfolioList.nein")}
				confirm={t("pages.portfolio.PortfolioList.yes")}
				height={230}
			/>
			<AuthAlert
				message={
					<>
						권한이 없습니다. <br />
						관리자에게 문의하세요.
					</>
				}
				type="warning"
				confirm="확인"
			/>
			{/* 포트폴리오 메모 */}
			{/* <PortfolioMemo /> */}
		</>
	);
};

export default PortfolioList;
